/**
 * ClusterBackground — Subtle color-tinted regions behind grouped nodes.
 *
 * Phase 4.3 visual: 20px border radius, ~8% alpha colour fill (alpha is baked
 * into the cluster.color rgba string by useClusterManager), pointer-events
 * disabled. Smooth transition when bounds change so nodes joining/leaving a
 * cluster doesn't jolt the layout.
 *
 * Sprint 10.2: rendered into React Flow's `<ViewportPortal>` (was
 * `<Panel>`), so the rectangles share the transformed viewport with the
 * nodes — they now actually pan + zoom together. `cluster.bounds.{x,y}` are
 * flow coordinates so no math change is needed.
 */

import type { CanvasCluster } from '@shared/types.ts';

interface ClusterBackgroundProps {
  clusters: CanvasCluster[];
  /** Whether clusters just appeared (for fade-in animation) */
  isAnimating: boolean;
}

export function ClusterBackground({ clusters, isAnimating }: ClusterBackgroundProps) {
  if (clusters.length === 0) return null;

  return (
    <>
      {clusters.map((cluster) => (
        <div
          key={cluster.id}
          className="pointer-events-none absolute"
          style={{
            left: cluster.bounds.x,
            top: cluster.bounds.y,
            width: cluster.bounds.width,
            height: cluster.bounds.height,
            backgroundColor: cluster.color,
            borderRadius: 20,
            opacity: isAnimating ? 0 : 1,
            animation: isAnimating ? 'fadeIn 0.2s ease-out forwards' : undefined,
            animationDelay: isAnimating ? '0.4s' : undefined,
            transition: 'left 300ms ease, top 300ms ease, width 300ms ease, height 300ms ease',
          }}
        />
      ))}
    </>
  );
}
